import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, Menu, X } from "lucide-react";
import { fileUrl } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";

export default function Shell({ nav = [], active, onNavigate, title, badge, logoId, children, fluid = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 z-40 h-screen w-64 shrink-0 border-r border-border bg-[#0d1420] flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-5 border-b border-border">
          <div className="bg-white rounded-lg px-3 py-2 inline-flex">
            <img src="/velopaths-logo.png" alt="Velopaths" className="h-6 w-auto" />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-primary font-mono mt-2">{badge || "Partner"}</div>
          {logoId && (
            <div className="flex items-center gap-2 mt-3">
              <img src={fileUrl(logoId)} alt="shop logo" className="w-6 h-6 rounded object-cover border border-border" data-testid="shop-sidebar-logo" />
              <span className="text-xs text-muted-foreground truncate">Your shop logo</span>
            </div>
          )}
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              onClick={() => {
                onNavigate(item.key);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active === item.key
                  ? "bg-primary/15 text-primary"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              }`}
            >
              <item.icon className="w-4 h-4" />
              <span className="flex-1 text-left">{item.label}</span>
              {item.count > 0 && (
                <span
                  data-testid={`nav-${item.key}-badge`}
                  className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-black text-[11px] font-bold flex items-center justify-center"
                >
                  {item.count}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 text-xs text-muted-foreground truncate">{user?.email}</div>
          <Button variant="ghost" size="sm" onClick={doLogout} data-testid="logout-button" className="w-full justify-start text-muted-foreground hover:text-destructive">
            <LogOut className="w-4 h-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-20 glass border-b border-border px-4 sm:px-8 py-4 flex items-center gap-3">
          <button className="lg:hidden text-foreground" onClick={() => setOpen(!open)}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <h1 className="font-heading text-xl sm:text-2xl font-bold min-w-0 truncate [[data-assistant-dock=header]_&]:pr-14 lg:[[data-assistant-dock=header]_&]:pr-0">{title}</h1>
          {["shop", "advertiser"].includes(user?.role) && (
            <div className="ml-auto"><NotificationBell /></div>
          )}
        </header>
        <main className={`flex-1 p-4 sm:p-8 w-full mx-auto animate-fade-up ${fluid ? "max-w-none" : "max-w-7xl"}`}>{children}</main>
      </div>
    </div>
  );
}
